import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { login, register, resendVerification } from '../services/api';
import { useLang } from '../context/LanguageContext';
import ForgotPasswordModal from './ForgotPasswordModal';

export default function AuthForm({ onBack, onSuccess, defaultRole }) {
  const [mode, setMode]         = useState('login');
  const [name, setName]         = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole]         = useState(defaultRole || 'CLIENT');
  const [loading, setLoading]   = useState(false);
  const [resending, setResending] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState(null);
  const [unverified, setUnverified] = useState(false);

  const { t } = useLang();
  const a = t.auth;

  React.useEffect(() => { if (defaultRole) setRole(defaultRole); }, [defaultRole]);

  const switchMode = (m) => {
    setMode(m);
    setUnverified(false);
    setRegisteredEmail(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (mode === 'register' && password.length < 6) {
      toast.error(a.passwordMinLength);
      setLoading(false);
      return;
    }
    if (mode === 'register') {
      try {
        await register({ name, username, password, role });
        setRegisteredEmail(username);
      } catch (err) {
        toast.error(err.response?.data?.message || a.genericError);
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const res = await login({ username, password });
      applySession(res.data);
    } catch (err) {
      setUnverified(err.response?.status === 403);
      toast.error(err.response?.data?.message || a.genericError);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await resendVerification(username);
      toast.success(a.resendSent);
    } catch {
      toast.error(a.resendError);
    } finally {
      setResending(false);
    }
  };

  const applySession = ({ token, userId, name: userName, role: userRole }) => {
    localStorage.setItem('taxi-token', token);
    localStorage.setItem('taxi-user', JSON.stringify({ id: userId, name: userName, role: userRole, username }));
    onSuccess({ id: userId, name: userName, role: userRole, username });
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12 relative"
      style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1638212476673-ba63c7ef34d8?auto=format&fit=crop&w=1920&q=80)', backgroundSize: 'cover', backgroundPosition: 'center' }}
    >
      <div className="absolute inset-0 bg-slate-900/75" />

      <div className="relative z-10 w-full max-w-sm">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-white/50 hover:text-white mb-8 transition-colors">
          {a.back}
        </button>

        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-8">
          <h2 className="text-2xl font-bold text-white mb-1">
            {registeredEmail ? a.checkEmailTitle : (mode === 'login' ? a.loginTitle : a.registerTitle)}
          </h2>
          {!registeredEmail && (
            <p className="text-sm text-white/50 mb-6">
              {mode === 'login' ? a.loginSubtitle : (role === 'CLIENT' ? a.joinAsRider : a.joinAsDriver)}
            </p>
          )}

          {registeredEmail ? (
            <>
              <div className="bg-white/10 border border-white/20 text-white/70 text-sm px-4 py-3 rounded-xl mb-4 mt-4">
                {a.checkEmailBefore}<strong className="text-white">{registeredEmail}</strong>{a.checkEmailAfter}
              </div>
              <button
                onClick={() => switchMode('login')}
                className="btn-primary w-full py-3 justify-center"
              >
                {a.backToSignIn}
              </button>
            </>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              {mode === 'register' && (
                <>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={a.namePlaceholder}
                    required
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
                  />
                  <div className="flex gap-2">
                    {['CLIENT', 'DRIVER'].map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setRole(r)}
                        className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all duration-200 ${role === r ? 'border-primary-500 bg-primary-600 text-white' : 'border-white/20 text-white/50 hover:border-white/40 hover:text-white'}`}
                      >
                        {r === 'CLIENT' ? a.roleRider : a.roleDriver}
                      </button>
                    ))}
                  </div>
                </>
              )}

              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={mode === 'register' ? a.emailPlaceholder : a.usernamePlaceholder}
                required
                type={mode === 'register' ? 'email' : 'text'}
                autoComplete="username"
                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
              />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={a.passwordPlaceholder}
                required
                autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
              />

              {mode === 'login' && unverified && (
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                  className="text-xs text-primary-400 hover:text-primary-300 underline disabled:opacity-50"
                >
                  {resending ? a.loading : a.resendLink}
                </button>
              )}

              {mode === 'login' && (
                <div className="text-right -mt-1">
                  <button
                    type="button"
                    onClick={() => setShowForgot(true)}
                    className="text-xs text-white/40 hover:text-white transition-colors"
                  >
                    {a.forgotPassword}
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-3 hover:scale-[1.02] hover:shadow-lg hover:shadow-primary-600/40 disabled:opacity-50 disabled:hover:scale-100"
              >
                {loading ? a.loading : mode === 'login' ? a.signIn : a.createAccount}
              </button>
            </form>
          )}

          {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}

          {!registeredEmail && (
            <p className="text-center text-sm text-white/40 mt-6">
              {mode === 'login' ? a.noAccount : a.hasAccount}{' '}
              <button
                onClick={() => switchMode(mode === 'login' ? 'register' : 'login')}
                className="font-semibold text-primary-400 hover:text-primary-300 transition-colors"
              >
                {mode === 'login' ? a.signUp : a.signIn}
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
